import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Strategy = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [activePhase, setActivePhase] = useState(0);
  const [selectedCaseStudy, setSelectedCaseStudy] = useState(0);
  const [activeStrategy, setActiveStrategy] = useState(0);

  // Strategic Phases Data
  const campaignPhases = [
    {
      phase: 1,
      title: 'Pre-Campaign Analysis',
      duration: '3-6 months before',
      description: 'Comprehensive research and strategic foundation',
      color: 'bg-blue-600',
      gradient: 'from-blue-500 to-blue-700',
      activities: [
        'Constituency demographic analysis',
        'Opposition research and SWOT analysis',
        'Voter sentiment mapping',
        'Historical voting pattern analysis',
        'Key stakeholder identification',
        'Resource requirement assessment'
      ],
      outcomes: [
        '95% accurate voter profiling',
        'Strategic positioning framework',
        'Campaign budget optimization',
        'Risk mitigation strategies'
      ]
    },
    {
      phase: 2,
      title: 'Brand & Message Development',
      duration: '2-4 months before',
      description: 'Creating compelling political identity and messaging',
      color: 'bg-red-600',
      gradient: 'from-red-500 to-red-700',
      activities: [
        'Political brand identity creation',
        'Core message development',
        'Manifesto drafting and refinement',
        'Visual identity design',
        'Slogan and tagline creation',
        'Communication strategy framework'
      ],
      outcomes: [
        'Distinctive brand recognition',
        'Clear value proposition',
        'Consistent messaging across platforms',
        'Emotional voter connection'
      ]
    },
    {
      phase: 3,
      title: 'Digital Strategy Implementation',
      duration: '1-3 months before',
      description: 'Comprehensive digital campaign execution',
      color: 'bg-purple-600',
      gradient: 'from-purple-500 to-purple-700',
      activities: [
        'Social media strategy execution',
        'Content calendar development',
        'Digital advertising campaigns',
        'Influencer partnership programs',
        'Online reputation management',
        'Viral content creation'
      ],
      outcomes: [
        '500M+ digital impressions',
        '85% positive sentiment',
        '300% follower growth',
        'Trending hashtag campaigns'
      ]
    },
    {
      phase: 4,
      title: 'Ground Campaign Mobilization',
      duration: '1-2 months before',
      description: 'Grassroots organizing and voter outreach',
      color: 'bg-green-600',
      gradient: 'from-green-500 to-green-700',
      activities: [
        'Volunteer recruitment and training',
        'Booth-level organization',
        'Door-to-door campaigning',
        'Community event organization',
        'Local leader engagement',
        'Voter registration drives'
      ],
      outcomes: [
        '25,000+ trained volunteers',
        '500+ booth coverage',
        '80% voter reach',
        'Strong grassroots network'
      ]
    },
    {
      phase: 5,
      title: 'Final Push & Election Day',
      duration: 'Last 30 days',
      description: 'Intensive campaigning and election execution',
      color: 'bg-orange-600',
      gradient: 'from-orange-500 to-orange-700',
      activities: [
        'High-intensity rallies and events',
        'Last-mile voter persuasion',
        'Election day coordination',
        'Real-time monitoring systems',
        'Crisis management protocols',
        'Victory celebration planning'
      ],
      outcomes: [
        'Maximum voter turnout',
        'Seamless election day execution',
        'Real-time result tracking',
        'Victory margin optimization'
      ]
    }
  ];

  // Case Studies Data
  const successfulCampaigns = [
    {
      title: 'Assembly Election Victory - Uttar Pradesh',
      candidate: 'Shri Rajesh Kumar',
      constituency: 'Lucknow Central',
      year: '2022',
      margin: '45,000+ votes',
      turnout: '72%',
      image: '/politicalparty/BJP.jpg',
      challenge: 'Incumbent opposition with 15-year stronghold',
      strategy: [
        'Hyper-local digital targeting',
        'Youth-focused messaging',
        'Infrastructure development promises',
        'Community-specific outreach programs'
      ],
      results: [
        'Defeated 3-term incumbent',
        'Highest voter turnout in 20 years',
        'Won 85% of youth votes',
        'Secured all major community endorsements'
      ],
      testimonial: "Jet Digital's strategic approach transformed our campaign from underdog to victory. Their data-driven methodology and ground execution were exceptional.",
      metrics: {
        digitalReach: '2.5M',
        volunteers: '5,000',
        events: '150+',
        mediaValue: '₹50L'
      }
    },
    {
      title: 'Lok Sabha Triumph - Maharashtra',
      candidate: 'Dr. Priya Sharma',
      constituency: 'Pune',
      year: '2024',
      margin: '1,20,000+ votes',
      turnout: '68%',
      image: '/politicalparty/inc.png',
      challenge: 'First-time candidate against established political dynasty',
      strategy: [
        'Women empowerment messaging',
        'Technology-driven governance promises',
        'Anti-corruption campaign',
        'Professional background highlighting'
      ],
      results: [
        'Largest victory margin in constituency history',
        'Won 78% of women votes',
        'Secured urban and rural support',
        'Created new voter coalition'
      ],
      testimonial: "The strategic brilliance of Jet Digital helped us connect with every voter segment. Their innovative approach made the impossible possible.",
      metrics: {
        digitalReach: '4.2M',
        volunteers: '8,500',
        events: '200+',
        mediaValue: '₹1.2Cr'
      }
    },
    {
      title: 'Municipal Corporation Success - Gujarat',
      candidate: 'Shri Amit Patel',
      constituency: 'Ahmedabad Municipal Corporation',
      year: '2023',
      margin: '25,000+ votes',
      turnout: '65%',
      image: '/politicalparty/BJP.jpg',
      challenge: 'Anti-incumbency and urban development issues',
      strategy: [
        'Smart city vision presentation',
        'Citizen engagement platforms',
        'Transparent governance promises',
        'Local business community outreach'
      ],
      results: [
        'Overcame anti-incumbency wave',
        'Won majority in all wards',
        'Secured business community support',
        'Highest satisfaction ratings'
      ],
      testimonial: "Jet Digital's local governance strategy helped us address every citizen concern effectively. Their approach was comprehensive and result-oriented.",
      metrics: {
        digitalReach: '1.8M',
        volunteers: '3,200',
        events: '120+',
        mediaValue: '₹35L'
      }
    }
  ];

  // Strategic Methodologies
  const strategicMethodologies = [
    {
      title: 'Data-Driven Voter Analysis',
      icon: '📊',
      description: 'Advanced analytics for precise voter targeting',
      techniques: [
        'Demographic segmentation analysis',
        'Psychographic profiling',
        'Voting behavior prediction models',
        'Sentiment analysis algorithms',
        'Real-time polling integration'
      ],
      successRate: '92%',
      impact: 'Increased targeting accuracy by 300%'
    },
    {
      title: 'Multi-Channel Communication',
      icon: '📱',
      description: 'Integrated messaging across all platforms',
      techniques: [
        'Social media ecosystem management',
        'Traditional media coordination',
        'WhatsApp broadcast campaigns',
        'SMS and voice call automation',
        'Print and outdoor advertising'
      ],
      successRate: '88%',
      impact: 'Achieved 95% message consistency'
    },
    {
      title: 'Grassroots Mobilization',
      icon: '👥',
      description: 'Community-level organization and engagement',
      techniques: [
        'Volunteer network development',
        'Community leader engagement',
        'Booth-level organization',
        'Local influencer partnerships',
        'Cultural and religious outreach'
      ],
      successRate: '94%',
      impact: 'Built networks in 100% constituencies'
    },
    {
      title: 'Crisis Management & Rapid Response',
      icon: '⚡',
      description: 'Real-time issue management and response',
      techniques: [
        '24/7 monitoring systems',
        'Rapid response protocols',
        'Damage control strategies',
        'Counter-narrative development',
        'Stakeholder communication'
      ],
      successRate: '96%',
      impact: 'Resolved 98% of issues within 4 hours'
    }
  ];

  // Target Audience Analysis
  const audienceSegments = [
    {
      segment: 'Youth Voters (18-35)',
      percentage: '35%',
      characteristics: ['Digital-first', 'Career-focused', 'Change-oriented'],
      strategies: ['Social media campaigns', 'Job creation messaging', 'Innovation promises'],
      channels: ['Instagram', 'YouTube', 'LinkedIn', 'Campus events']
    },
    {
      segment: 'Middle-aged Professionals (36-55)',
      percentage: '40%',
      characteristics: ['Stability-seeking', 'Family-oriented', 'Economic-focused'],
      strategies: ['Economic development', 'Infrastructure promises', 'Family welfare'],
      channels: ['Facebook', 'WhatsApp', 'Local newspapers', 'Community meetings']
    },
    {
      segment: 'Senior Citizens (55+)',
      percentage: '25%',
      characteristics: ['Traditional values', 'Security-conscious', 'Experience-valued'],
      strategies: ['Healthcare focus', 'Pension schemes', 'Traditional values'],
      channels: ['Television', 'Radio', 'Print media', 'Community centers']
    }
  ];

  // Fundraising Strategies
  const fundraisingApproaches = [
    {
      method: 'Digital Fundraising',
      description: 'Online donation platforms and campaigns',
      techniques: ['Crowdfunding campaigns', 'Social media drives', 'Email marketing', 'Mobile payment integration'],
      averageRaise: '₹25-50L',
      successRate: '78%'
    },
    {
      method: 'Corporate Partnerships',
      description: 'Business community engagement and support',
      techniques: ['Industry roundtables', 'Business leader meetings', 'CSR alignment', 'Economic policy discussions'],
      averageRaise: '₹50L-2Cr',
      successRate: '85%'
    },
    {
      method: 'Community Fundraising',
      description: 'Grassroots donation collection',
      techniques: ['Local events', 'Community gatherings', 'Cultural programs', 'Religious institution partnerships'],
      averageRaise: '₹10-25L',
      successRate: '92%'
    }
  ];

  useEffect(() => {
    if (inView) {
      const interval = setInterval(() => {
        setActivePhase((prev) => (prev + 1) % campaignPhases.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [inView, campaignPhases.length]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-gray-900 to-red-900 text-white py-32">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="mb-8"
          >
            <span className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 mb-8">
              <span className="text-xl">🎯</span>
              <span className="font-semibold tracking-wide">WINNING ELECTION STRATEGIES</span>
            </span>
          </motion.div>

          <motion.h1
            className="hero-text mb-8"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Our Proven Political
            <span className="text-gradient-victory block mt-2">Strategy Framework</span>
          </motion.h1>

          <motion.p
            className="text-xl text-white/90 max-w-4xl mx-auto leading-relaxed mb-12"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Discover the comprehensive strategic methodologies that have delivered victory in 200+ elections across India.
            Our data-driven approach combines traditional campaigning with cutting-edge digital strategies.
          </motion.p>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-400 mb-2">200+</div>
              <div className="text-white/80">Successful Campaigns</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-2">92%</div>
              <div className="text-white/80">Success Rate</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-400 mb-2">15+</div>
              <div className="text-white/80">States Covered</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-400 mb-2">50M+</div>
              <div className="text-white/80">Voters Reached</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Campaign Phases Section */}
      <section ref={ref} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.2 }}
          >
            <h2 className="display-text mb-6">
              Strategic Campaign
              <span className="text-gradient-political block mt-2">Implementation Phases</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our systematic 5-phase approach ensures comprehensive campaign coverage from initial analysis to victory celebration.
            </p>
          </motion.div>

          {/* Interactive Phase Timeline */}
          <div className="mb-16">
            <div className="flex justify-center mb-8">
              <div className="flex space-x-4 bg-gray-100 rounded-full p-2 overflow-hidden">
                {campaignPhases.map((phase, index) => (
                  <button
                    key={phase.phase}
                    onClick={() => setActivePhase(index)}
                    className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                      activePhase === index
                        ? 'bg-white text-blue-600 shadow-lg'
                        : 'text-gray-600 hover:text-blue-600'
                    }`}
                  >
                    Phase {phase.phase}
                  </button>
                ))}
              </div>
            </div>

            <motion.div
              key={activePhase}
              className="bg-white rounded-2xl shadow-xl p-8"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <div className={`inline-flex items-center space-x-3 ${campaignPhases[activePhase].gradient} bg-gradient-to-r text-white rounded-full px-6 py-3 mb-6`}>
                    <span className="font-bold">Phase {campaignPhases[activePhase].phase}</span>
                    <span>•</span>
                    <span>{campaignPhases[activePhase].duration}</span>
                  </div>

                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    {campaignPhases[activePhase].title}
                  </h3>

                  <p className="text-lg text-gray-600 mb-8">
                    {campaignPhases[activePhase].description}
                  </p>

                  <div className="mb-8">
                    <h4 className="text-xl font-semibold text-gray-900 mb-4">Key Activities</h4>
                    <div className="space-y-3">
                      {campaignPhases[activePhase].activities.map((activity, index) => (
                        <div key={index} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-700">{activity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-6">Expected Outcomes</h4>
                  <div className="space-y-4">
                    {campaignPhases[activePhase].outcomes.map((outcome, index) => (
                      <motion.div
                        key={index}
                        className="flex items-center space-x-4 p-4 bg-green-50 rounded-lg"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                      >
                        <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-sm">✓</span>
                        </div>
                        <span className="text-gray-800 font-medium">{outcome}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Strategic Methodologies Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.2 }}
          >
            <h2 className="display-text mb-6">
              Core Strategic
              <span className="text-gradient-victory block mt-2">Methodologies</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Our proven methodologies that consistently deliver electoral success across diverse political landscapes.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {strategicMethodologies.map((methodology, index) => (
              <motion.div
                key={methodology.title}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-8"
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-center space-x-4 mb-6">
                  <div className="text-4xl">{methodology.icon}</div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{methodology.title}</h3>
                    <p className="text-white/70">{methodology.description}</p>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-semibold text-white mb-3">Implementation Techniques</h4>
                  <div className="space-y-2">
                    {methodology.techniques.map((technique, techIndex) => (
                      <div key={techIndex} className="flex items-start space-x-2">
                        <span className="text-blue-400 mt-1">•</span>
                        <span className="text-white/80 text-sm">{technique}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-white/20">
                  <div>
                    <div className="text-2xl font-bold text-green-400">{methodology.successRate}</div>
                    <div className="text-white/60 text-sm">Success Rate</div>
                  </div>
                  <div className="text-right">
                    <div className="text-white/80 text-sm font-medium">{methodology.impact}</div>
                    <div className="text-white/60 text-xs">Key Impact</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.2 }}
          >
            <h2 className="display-text mb-6">
              Victorious Campaign
              <span className="text-gradient-political block mt-2">Case Studies</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real success stories showcasing our strategic excellence and measurable results across different electoral contexts.
            </p>
          </motion.div>

          {/* Case Study Selector */}
          <div className="flex justify-center mb-12">
            <div className="flex space-x-4 bg-gray-100 rounded-full p-2">
              {successfulCampaigns.map((campaign, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedCaseStudy(index)}
                  className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                    selectedCaseStudy === index
                      ? 'bg-white text-blue-600 shadow-lg'
                      : 'text-gray-600 hover:text-blue-600'
                  }`}
                >
                  {campaign.constituency}
                </button>
              ))}
            </div>
          </div>

          {/* Case Study Details */}
          <motion.div
            key={selectedCaseStudy}
            className="bg-white rounded-2xl shadow-xl overflow-hidden"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-8">
              {/* Campaign Overview */}
              <div className="lg:col-span-2">
                <div className="flex items-center space-x-4 mb-6">
                  <img
                    src={successfulCampaigns[selectedCaseStudy].image}
                    alt="Party Logo"
                    className="w-16 h-16 object-contain rounded-lg"
                  />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {successfulCampaigns[selectedCaseStudy].title}
                    </h3>
                    <p className="text-gray-600">
                      {successfulCampaigns[selectedCaseStudy].candidate} • {successfulCampaigns[selectedCaseStudy].year}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {successfulCampaigns[selectedCaseStudy].margin}
                    </div>
                    <div className="text-gray-600">Victory Margin</div>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {successfulCampaigns[selectedCaseStudy].turnout}
                    </div>
                    <div className="text-gray-600">Voter Turnout</div>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Challenge</h4>
                  <p className="text-gray-700 bg-red-50 p-4 rounded-lg">
                    {successfulCampaigns[selectedCaseStudy].challenge}
                  </p>
                </div>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Strategic Approach</h4>
                  <div className="space-y-2">
                    {successfulCampaigns[selectedCaseStudy].strategy.map((item, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Results Achieved</h4>
                  <div className="space-y-2">
                    {successfulCampaigns[selectedCaseStudy].results.map((result, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs">✓</span>
                        </div>
                        <span className="text-gray-700">{result}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Metrics & Testimonial */}
              <div>
                <div className="bg-gray-50 p-6 rounded-xl mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Campaign Metrics</h4>
                  <div className="space-y-4">
                    <div>
                      <div className="text-xl font-bold text-blue-600">
                        {successfulCampaigns[selectedCaseStudy].metrics.digitalReach}
                      </div>
                      <div className="text-gray-600 text-sm">Digital Reach</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-green-600">
                        {successfulCampaigns[selectedCaseStudy].metrics.volunteers}
                      </div>
                      <div className="text-gray-600 text-sm">Volunteers Mobilized</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-purple-600">
                        {successfulCampaigns[selectedCaseStudy].metrics.events}
                      </div>
                      <div className="text-gray-600 text-sm">Events Organized</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-orange-600">
                        {successfulCampaigns[selectedCaseStudy].metrics.mediaValue}
                      </div>
                      <div className="text-gray-600 text-sm">Media Value Generated</div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Client Testimonial</h4>
                  <blockquote className="text-gray-700 italic">
                    "{successfulCampaigns[selectedCaseStudy].testimonial}"
                  </blockquote>
                  <div className="mt-4 text-sm text-gray-600">
                    - {successfulCampaigns[selectedCaseStudy].candidate}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Target Audience Analysis Section */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.2 }}
          >
            <h2 className="display-text mb-6">
              Strategic Voter
              <span className="text-gradient-political block mt-2">Segmentation Analysis</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Understanding and targeting diverse voter segments with tailored messaging and channel strategies.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {audienceSegments.map((segment, index) => (
              <motion.div
                key={segment.segment}
                className="bg-white rounded-xl p-8 shadow-lg"
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-blue-600 mb-2">{segment.percentage}</div>
                  <h3 className="text-xl font-semibold text-gray-900">{segment.segment}</h3>
                </div>

                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Characteristics</h4>
                  <div className="space-y-2">
                    {segment.characteristics.map((char, charIndex) => (
                      <span key={charIndex} className="inline-block bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full mr-2 mb-2">
                        {char}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Key Strategies</h4>
                  <div className="space-y-2">
                    {segment.strategies.map((strategy, stratIndex) => (
                      <div key={stratIndex} className="flex items-start space-x-2">
                        <span className="text-green-600 mt-1">•</span>
                        <span className="text-gray-700 text-sm">{strategy}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Primary Channels</h4>
                  <div className="flex flex-wrap gap-2">
                    {segment.channels.map((channel, channelIndex) => (
                      <span key={channelIndex} className="bg-gray-100 text-gray-700 text-xs px-3 py-1 rounded-full">
                        {channel}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Fundraising Strategies Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.2 }}
          >
            <h2 className="display-text mb-6">
              Strategic Campaign
              <span className="text-gradient-political block mt-2">Fundraising Approaches</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive fundraising strategies that ensure adequate campaign resources while maintaining transparency and compliance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {fundraisingApproaches.map((approach, index) => (
              <motion.div
                key={approach.method}
                className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-8 shadow-lg border border-blue-100"
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <h3 className="text-xl font-bold text-gray-900 mb-4">{approach.method}</h3>
                <p className="text-gray-600 mb-6">{approach.description}</p>

                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Implementation Techniques</h4>
                  <div className="space-y-2">
                    {approach.techniques.map((technique, techIndex) => (
                      <div key={techIndex} className="flex items-start space-x-2">
                        <span className="text-blue-600 mt-1">•</span>
                        <span className="text-gray-700 text-sm">{technique}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <div>
                    <div className="text-lg font-bold text-green-600">{approach.averageRaise}</div>
                    <div className="text-gray-500 text-xs">Average Raise</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-blue-600">{approach.successRate}</div>
                    <div className="text-gray-500 text-xs">Success Rate</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-gray-900 to-red-900 text-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.2 }}
          >
            <h2 className="display-text mb-6">
              Ready to Implement
              <span className="text-gradient-victory block mt-2">Winning Strategies?</span>
            </h2>
            <p className="text-xl text-white/90 mb-12 max-w-3xl mx-auto">
              Join 200+ successful candidates who have achieved electoral victory with our proven strategic framework.
              Let's discuss your campaign goals and create a customized winning strategy.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <motion.button
                className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  const element = document.getElementById('contact');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                Book Strategy Consultation
              </motion.button>

              <motion.button
                className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 hover:bg-white/20"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  window.open('/portfolio', '_blank');
                }}
              >
                View Success Portfolio
              </motion.button>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-2xl font-bold text-orange-400 mb-2">24/7</div>
                <div className="text-white/80">Campaign Support</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white mb-2">100%</div>
                <div className="text-white/80">Confidential Process</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-400 mb-2">30 Days</div>
                <div className="text-white/80">Strategy Development</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Strategy;
